import { useEffect, useState } from 'react';
import { Check, Clock, Landmark } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';

/**
 * LOS DOS RELOJES DEL CHEQUE, por etapa — pedido de Manuel (2026-08-19): "en payments, debajo
 * del payment summary, las gráficas de los check dates, uno al lado del otro: ACV, supplements
 * y depreciación; en cuántos días se obtuvo la fecha después de aprobado y en cuántos días llegó
 * el cheque, barras hacia arriba, y un indicativo de si ya llegó o si estamos esperando".
 *
 * Son dos esperas DISTINTAS y por eso son dos barras:
 *  · CONSEGUIR LA FECHA: lo que tardamos en que el carrier dijera cuándo mandó el cheque. Es el
 *    tramo que el equipo puede mover, y por eso va primero.
 *  · QUE LLEGUE: hasta que el cheque estuvo en la mano. Incluye correo y banco.
 *
 * Las seis barras comparten escala (el máximo de la tarjeta más lenta, con piso de 30 días) para
 * que se puedan comparar de un vistazo: sin eso, tres gráficas con escalas propias hacen que
 * 9 días y 90 se vean igual de altas.
 */
type Clock = {
  stage: 'acv' | 'supplement' | 'depreciation';
  anchor_at: string | null;
  anchor_kind: 'status' | 'approved' | 'supplement_approved' | 'coc' | 'issued' | null;
  sent_on: string | null;
  received_on: string | null;
  days_to_date: number | null;
  /** El número de la primera barra es aproximado (registro histórico, ver backend). */
  date_estimated?: boolean;
  days_to_check: number | null;
  days_waiting: number | null;
  state: 'received' | 'waiting' | 'none';
  amount_cents: number | null;
};

const TITULO: Record<Clock['stage'], string> = {
  acv: 'cd.stageAcv', supplement: 'cd.stageSupplement', depreciation: 'cd.stageDepreciation',
};
const ANCLA: Record<NonNullable<Clock['anchor_kind']>, string> = {
  status: 'clk.fromQueue', approved: 'clk.fromApproved', supplement_approved: 'clk.fromSuppApproved', coc: 'clk.fromCoc', issued: 'clk.fromIssued',
};
const ALTO = 92; // px de la barra más alta

export function CheckClocks({ claimId }: { claimId: string }) {
  const { t } = useI18n();
  const [clocks, setClocks] = useState<Clock[] | null>(null);
  useEffect(() => {
    let vivo = true;
    api.get<{ clocks: Clock[] }>(`/api/claims/${claimId}/check-clocks`)
      .then((r) => { if (vivo) setClocks(r.clocks); })
      .catch(() => { if (vivo) setClocks([]); });
    return () => { vivo = false; };
  }, [claimId]);

  if (!clocks) return null;
  // Sin una sola etapa con datos no se dibuja nada: tres tarjetas vacías son ruido.
  if (!clocks.some((c) => c.state !== 'none')) return null;

  const maximo = Math.max(
    30,
    ...clocks.flatMap((c) => [c.days_to_date ?? 0, c.days_to_check ?? 0, c.days_waiting ?? 0]),
  );

  return (
    <div className="card" style={{ display: 'grid', gap: 10 }}>
      <div>
        <h2 className="card-title" style={{ margin: 0 }}>
          <Clock size={15} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('clk.title')}
        </h2>
        <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 2 }}>{t('clk.help')}</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10 }}>
        {clocks.map((c) => <Tarjeta key={c.stage} c={c} maximo={maximo} />)}
      </div>
    </div>
  );
}

function Tarjeta({ c, maximo }: { c: Clock; maximo: number }) {
  const { t } = useI18n();
  const vacia = c.state === 'none';
  // Esperando: la segunda barra muestra lo que YA se lleva esperando, en ámbar y punteada, para
  // que se lea "esto sigue corriendo" y no como un dato cerrado.
  const segundo = c.state === 'received' ? c.days_to_check : c.days_waiting;
  const esperando = c.state === 'waiting';

  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 12, padding: 12, display: 'grid', gap: 8, background: 'var(--surface-2)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <Landmark size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom', color: 'var(--muted)' }} />
        <b style={{ fontSize: 'var(--fs-sm)' }}>{t(TITULO[c.stage])}</b>
        <span style={{ flex: 1 }} />
        {c.state === 'received' && (
          <span className="badge" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'], background: 'var(--green-soft)', color: 'var(--green)' }}>
            <Check size={11} strokeWidth={2.25} /> {t('clk.arrived')}
          </span>
        )}
        {esperando && (
          <span className="badge" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'], background: 'var(--orange-soft, #fef3c7)', color: 'var(--orange, #b45309)' }}>
            <Clock size={11} strokeWidth={2.25} /> {t('clk.waiting')}
          </span>
        )}
        {vacia && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('clk.noCheck')}</span>}
      </div>

      {vacia ? (
        <div className="muted" style={{ fontSize: 'var(--fs-2xs)', minHeight: ALTO + 26, display: 'grid', placeItems: 'center' }}>—</div>
      ) : (
        <>
          {/* Las barras SUBEN desde una línea base común. */}
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 18, height: ALTO, borderBottom: '2px solid var(--line)', paddingBottom: 0 }}>
            <Barra dias={c.days_to_date} maximo={maximo} color="var(--accent)" aproximada={c.date_estimated} />
            <Barra dias={segundo} maximo={maximo} color={esperando ? 'var(--orange, #b45309)' : 'var(--green)'} punteada={esperando} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 18, textAlign: 'center' }}>
            <Pie label={t('clk.toDate')} dias={c.days_to_date} aprox={c.date_estimated} />
            <Pie label={esperando ? t('clk.waitingSoFar') : t('clk.toCheck')} dias={segundo} />
          </div>
          {c.anchor_kind && (
            <div className="muted" style={{ fontSize: 'var(--fs-2xs)', textAlign: 'center' }}>{t(ANCLA[c.anchor_kind])}</div>
          )}
          {!c.anchor_at && (
            // Sin fecha de aprobación no se pueden contar los días. Se dice, no se inventa.
            <div className="muted" style={{ fontSize: 'var(--fs-2xs)', textAlign: 'center' }}>{t('clk.noAnchor')}</div>
          )}
        </>
      )}
    </div>
  );
}

function Barra({ dias, maximo, color, punteada = false, aproximada = false }: { dias: number | null; maximo: number; color: string; punteada?: boolean; aproximada?: boolean }) {
  const { t } = useI18n();
  if (dias == null) {
    return <div style={{ width: 42, height: 4, background: 'var(--line)', borderRadius: 2 }} title={t('clk.noData')} />;
  }
  // Mínimo 6px: un 0 tiene que verse como barra, no como nada.
  const alto = Math.max(6, Math.round((dias / maximo) * ALTO));
  // Rayada = el número es aproximado (registro histórico) o el reloj sigue corriendo.
  const rayada = punteada || aproximada;
  return (
    <div style={{ width: 42, height: alto, borderRadius: '6px 6px 0 0', opacity: aproximada && !punteada ? 0.65 : 1,
      background: rayada ? `repeating-linear-gradient(45deg, ${color}, ${color} 4px, transparent 4px, transparent 8px)` : color,
      border: rayada ? `1px solid ${color}` : undefined }}
      title={`${dias}d${aproximada ? ` · ${t('clk.approxHint')}` : ''}`} />
  );
}

function Pie({ label, dias, aprox = false }: { label: string; dias: number | null; aprox?: boolean }) {
  const { t } = useI18n();
  return (
    <div style={{ width: 42 }}>
      <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'], fontFamily: 'var(--font-mono)' }}
        title={aprox ? t('clk.approxHint') : undefined}>
        {dias == null ? '—' : `${aprox ? '~' : ''}${dias}d`}
      </div>
      <div className="muted" style={{ fontSize: 'var(--fs-2xs)', lineHeight: 1.2 }}>{label}</div>
    </div>
  );
}
